import { useEffect, useState } from "react"
import PropTypes from "prop-types"
import classnames from "classnames"

import { gql, graphQueryBang } from "shared/lib/graphql"
import Spinner from "shared/components/spinner"

import dsv from "./discover_service_view.scss"
import ratings from "./ratings.scss"

import thumbsUp from "images/partners/feedback/thumbs-up.svg"
import thumbsDown from "images/partners/feedback/thumbs-down.svg"

const FeedbackQuery = gql`
  query($module_name: String) {
    channel(module_name: $module_name) {
      total_applet_likes
      total_applet_dislikes
      total_applet_opinion_codes {
        not_useful
        too_slow
        unexpected
        no_response
      }
    }
  }
`

const FEEDBACK_TYPES = {
  not_useful: "Not useful",
  too_slow: "Too slow",
  unexpected: "Unexpected behavior",
  no_response: "No response",
}

const UserFeedback = ({ moduleName }) => {
  const [loading, setLoading] = useState(true)
  const [thumbsUpCount, setThumbsUpCount] = useState()
  const [thumbsDownCount, setThumbsDownCount] = useState()
  const [thumbsDownReasons, setThumbsDownReasons] = useState()

  useEffect(() => {
    const effect = async () => {
      const {
        channel: {
          total_applet_likes,
          total_applet_dislikes,
          total_applet_opinion_codes,
        },
      } = await graphQueryBang(FeedbackQuery, { module_name: moduleName })
      setThumbsUpCount(total_applet_likes)
      setThumbsDownCount(total_applet_dislikes)
      setThumbsDownReasons(
        Object.entries(total_applet_opinion_codes).map(([reason, count]) => ({
          reason: FEEDBACK_TYPES[reason],
          count,
          percent: total_applet_dislikes
            ? Math.round(100 * (count / total_applet_dislikes))
            : 0,
        }))
      )
      setLoading(false)
    }
    effect()
  }, [])

  if (loading)
    return (
      <>
        <h3 styleName="dsv.header">User feedback</h3>
        <Spinner config={{ position: "relative" }} />
      </>
    )

  const totalRatings = thumbsUpCount + thumbsDownCount
  const positiveRatingPercentage = Math.round(
    100 * (thumbsUpCount / totalRatings)
  )

  return (
    <>
      <h3 styleName="dsv.header">User feedback</h3>
      <div styleName="dsv.chart-container ratings.ratings-split">
        <div styleName="ratings.applet-feedback-rating ratings.expanded">
          <div styleName="ratings.thumbs-container">
            <div styleName="ratings.positive-ratings">
              <div styleName="ratings.rating-container">
                <span
                  styleName="ratings.thumbs ratings.up"
                  dangerouslySetInnerHTML={{ __html: thumbsUp }}
                />
                <span styleName="ratings.thumb-count">{thumbsUpCount}</span>
              </div>
            </div>
            <div styleName="ratings.negative-ratings">
              <div styleName="ratings.rating-container">
                <span
                  styleName="ratings.thumbs ratings.down"
                  dangerouslySetInnerHTML={{ __html: thumbsDown }}
                />
                <span styleName="ratings.thumb-count">{thumbsDownCount}</span>
              </div>
            </div>
          </div>
          <div styleName="ratings.rating-bar">
            <span
              styleName={classnames("ratings.positive-rating-bar", {
                "ratings.zero": positiveRatingPercentage === 0,
              })}
              style={{ width: `${positiveRatingPercentage}%` }}
            >
              <span styleName="ratings.percent">
                {positiveRatingPercentage > 0 && `${positiveRatingPercentage}%`}
              </span>
            </span>
            <span
              styleName={classnames("ratings.negative-rating-bar", {
                "ratings.zero": positiveRatingPercentage === 100,
              })}
              style={{ width: `${100 - positiveRatingPercentage}%` }}
            >
              <span styleName="ratings.percent">
                {positiveRatingPercentage < 100 &&
                  `${100 - positiveRatingPercentage}%`}
              </span>
            </span>
          </div>
        </div>
        <div styleName="ratings.thumbs-down-reasons">
          {thumbsDownReasons.map(({ reason, percent, count }) => (
            <div key={reason} styleName="ratings.reason">
              <span>{reason}</span>
              <span styleName="ratings.outer-bar">
                <span
                  styleName="ratings.inner-bar"
                  style={{ width: `${percent}%` }}
                >
                  <span>{percent}%</span>
                </span>
              </span>
              <span styleName="ratings.count">{count}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

UserFeedback.propTypes = {
  moduleName: PropTypes.string.isRequired,
}

export default UserFeedback
